import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import { Card, CardContent } from "../../ui/card";
import {
    Monitor,
    Apple,
    ShieldCheck,
    Cpu,
    ChevronLeft,
    ChevronRight,
    Square,
    Terminal,
    Laptop,
    Smartphone,
    Loader2,
    Clock,
    UserCheck,
    Check
} from "lucide-react";
import { useLocation } from "wouter";
import { Device, DeviceLoadingState } from "../interfaces/EndpointInterfaces";
import StatusIndicator from "../Endpoints/StatusIndicator";
import RequestConnectAccessDialog from "./RequestConnectAccessDialog";
import { Skeleton } from "../../ui/skeleton"; // ✅ Correct import
import { getAllWindowsCredentials, getUsers } from "./Api/tenantuserendpointsapi";
import { myLocalStorage } from "../../StorageHelper";
import useUserStore from "../../../services/userStore";
import { FaLinux, FaWindows } from "react-icons/fa";
import { FaApple } from "react-icons/fa6";
import useNotificationStore from "../../../services/notificationStore";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { NGROK } from "../../../APIs";

interface TenantDeviceTableProps {
    devices: Device[];
    onConnect?: (device: Device, mode: string, label: string) => void;
    isLoading: boolean | false;
    totalDevicesCount: number;
    deviceLoadingState: Record<string, DeviceLoadingState>;
    page: number;
    setPage: React.Dispatch<React.SetStateAction<number>>;

}

const DEVICES_PER_PAGE = 15;

export default function TenantDeviceTable({
    devices,
    onConnect,
    isLoading,
    totalDevicesCount,
    deviceLoadingState,
    page,
    setPage
}: TenantDeviceTableProps) {
    const navigate = useNavigate();
    const [showAccessDialog, setShowAccessDialog] = useState(false);
    const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [usersList, setUsersList] = useState<any[]>([]);
    const [pendingRequestDeviceIds, setPendingRequestDeviceIds] = useState<Record<string, boolean>>({});
    let tenant = myLocalStorage.getItem("latestTenant");
    let tenantName = myLocalStorage.getItem("latestTenant")?.tenantName;
    let { email, role, userRole } = useUserStore((state) => state.user);
    const { userApprovedNotifications, setUserApprovedNotifications } =
        useNotificationStore();
    const isFetchingSummaryRef = useRef(false);
    const [remainingTimes, setRemainingTimes] = useState<Record<string, string>>({});
    const [remainingSeconds, setRemainingSeconds] = useState(30 * 60);

    const isOwnDevices = (device: Device): boolean => {
        return device.belongedToUser === email;
    }

    const getApprovedAccessNotification = (deviceId: string) => {
        return userApprovedNotifications.find(
            (item: any) =>
                Number(item.computerId) === Number(deviceId) &&
                item.requestStatus === "APPROVED" &&
                item.resourceType === "GROUP" &&
                item.resourceName === "Whiteswan Access"
        );
    };

    const hasPendingRequest = (deviceId: string) => pendingRequestDeviceIds[deviceId] === true;

    const shouldShowConnectButton = (device: Device): boolean => {
        const item = getApprovedAccessNotification(device.id);
        if (!item) {
            return false;
        }

        const nowUTC = moment.utc();

        if (item.schedules
            && item.schedules.length > 0) {
            const schedules = item.schedules;
            const isActive = schedules.some((schedule: any) => {
                const scheduledStartTime = moment.utc(schedule.scheduledStartTime);
                const scheduledEndTime = moment.utc(schedule.scheduledEndTime);
                const isWithinSchedule = nowUTC.isBetween(
                    scheduledStartTime,
                    scheduledEndTime,
                    null,
                    "[]",
                );
                return isWithinSchedule;
            });
            return isActive;

        }
        return true;

    }

    const refreshApprovedAccessNotifications = useCallback(async () => {
        if (!email || !tenantName || isFetchingSummaryRef.current) return;
        isFetchingSummaryRef.current = true;

        const controller = new AbortController();

        try {
            const params: Record<string, string> = {
                role,
                tenantName,
            };

            if (!email.startsWith("wsadmin")) {
                params.email = email;
            }

            const response = await axios.get(`${NGROK}/api/access/summary`, {
                params,
                signal: controller.signal,
            });

            if (response?.data?.onPremAccess) {
                setUserApprovedNotifications(response.data.onPremAccess);
            }
        } catch (error) {
            if (!axios.isCancel(error)) {
                console.error("Error fetching summary:", error);
            }
        } finally {
            isFetchingSummaryRef.current = false;
        }
    }, [email, role, tenantName, setUserApprovedNotifications]);

    const handleRequestSubmitSuccess = useCallback(async () => {
        if (!selectedDevice?.id) {
            await refreshApprovedAccessNotifications();
            return;
        }

        setPendingRequestDeviceIds((prev) => ({
            ...prev,
            [selectedDevice.id]: true,
        }));

        await refreshApprovedAccessNotifications();
    }, [refreshApprovedAccessNotifications, selectedDevice]);

    const isDeviceSSh = (device: Device) => {
        return (
            !device?.isManuallyAddedMachine &&
            (
                device?.OperatingSystem?.toLowerCase().includes("linux") ||
                device?.OperatingSystem?.toLowerCase().includes("macos")
            )
        );
    };

    const labelStatus = (mode: string, osmode: string) => {
        let status = mode
        if (mode === "INHERITED_FROM_PROFILE") status = osmode;
        if (mode === "INHERITED_FROM_PROFILE" && osmode === "INHERITED_FROM_TENANT") status = tenant.mode
        return status.toUpperCase();

    }

    const fetchAllUsers = async (device: Device) => {
        if (device?.isManuallyAddedMachine) {
            const response = await getAllWindowsCredentials(tenantName, device.id);
            setUsersList(response || []);
            return;
        }

        if (isDeviceSSh(device)) {

            let response = await getUsers(tenantName, device.id, email);
            if (response) {
                let content: any[] = response;

                if (userRole === "epam-user" ||
                    userRole === "server-pam-user" ||
                    userRole === "developer-user" ||
                    userRole === "thirdparty-user") {
                    content = response.filter((data: any) => data.cn !== "wsadmin");
                }
                setUsersList(content);
            }

        } else {
            let response = await getAllWindowsCredentials(tenantName, device.id);
            setUsersList(response);
        };
    }

    const isDeviceWindows = (device: Device): boolean => {
        if (device.OperatingSystem && device.OperatingSystem.trim().length > 0) {
            return device.OperatingSystem.toLowerCase().includes("windows");
        }
        return false;
    };

    const handleConnect = (deviceId: string) => {
        const device = devices.find((d) => d.id === deviceId);
        setUsersList([])
        if (!device) return;
        const deviceNotification = getApprovedAccessNotification(device.id);
        const canConnectImmediately = device?.hasAccess || shouldShowConnectButton(device);

        const mode = deviceNotification?.connectionMode || "";
        const label = deviceNotification?.connectionLabel || "";
        if (canConnectImmediately) {

            if (isDeviceWindows(device) || device?.isManuallyAddedMachine) {
                onConnect?.(device, mode, label);
            } else {
                onConnect?.(device, "", "");
            }

        } else if (device) {
            fetchAllUsers(device);
            setSelectedDevice(device);
            setShowAccessDialog(true);
        }

    };


    const getOSIcon = (osString: string) => {
        const os = osString.toLowerCase();
        const sizeClass = "w-7 h-7";

        if (os.includes("windows"))
            return <FaWindows className={`${sizeClass} text-blue-600 dark:text-blue-400`} />;
        if (os.includes("mac") || os.includes("darwin"))
            return <FaApple className={`${sizeClass} text-gray-800 dark:text-white`} />; // white in dark mode
        if (
            os.includes("ubuntu") ||
            os.includes("centos") ||
            os.includes("rhel") ||
            os.includes("rocky") ||
            os.includes("linux")
        )
            return <FaLinux className={`${sizeClass} text-black dark:text-white`} />; // white in dark mode

        return <FaLinux className={`${sizeClass} text-gray-500 dark:text-gray-400`} />;
    };


    const getOSType = (device: Device): "windows" | "linux" | "manual" => {
        if (device?.isManuallyAddedMachine) return "manual";
        const os = device.os.toLowerCase();
        if (os.includes("windows")) return "windows";
        if (os.includes("linux") || os.includes("ubuntu") || os.includes("mac") || os.includes("darwin"))
            return "linux";
        return "linux";
    };

    const handleDeviceClick = (deviceId: string) => {
        const device = devices.find((d) => d.id === deviceId);
        if (device) {
            if (device?.hasAccess || isOwnDevices(device))
                navigate(`/endpoints/tenantdevice/${deviceId}`, { state: { device } });
        }

    };

    function getRemainingTime(device: Device) {
        const deviceNotification = userApprovedNotifications.find(
            (item: any) =>
                item.computerId === Number(device.id) &&
                item.requestStatus === "APPROVED" &&
                item.resourceType === "GROUP" &&
                item.resourceName === "Whiteswan Access"
        );

        if (deviceNotification && deviceNotification.accessTimeOverAt) {
            // Ensure proper UTC parsing (append "Z" if backend doesn't include it)
            const accessTimeOverAt = new Date(
                deviceNotification.accessTimeOverAt.endsWith("Z")
                    ? deviceNotification.accessTimeOverAt
                    : `${deviceNotification.accessTimeOverAt}Z`
            );

            const now = new Date();

            const timeDiff = accessTimeOverAt.getTime() - now.getTime();

            if (timeDiff <= 0) {
                return "Expired";
            }

            const hours = Math.floor(timeDiff / (1000 * 60 * 60));
            const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
            const seconds = Math.floor((timeDiff % (1000 * 60)) / 1000);

            return `${hours}h ${minutes}m ${seconds}s`;
        }

        return null;
    }


    useEffect(() => {
        const updateAllRemainingTimes = () => {
            setRemainingTimes((prev) => {
                const updated: Record<string, string> = {};

                devices.forEach((device) => {
                    const time = getRemainingTime(device);
                    if (time && time !== "Expired") {
                        updated[device.id] = time;
                    }
                });

                return updated;
            });
        };

        // Run immediately and then every second
        updateAllRemainingTimes();
        const interval = setInterval(updateAllRemainingTimes, 1000);

        return () => clearInterval(interval);
    }, [devices, userApprovedNotifications]);

    useEffect(() => {
        if (!email || !tenantName) return;

        refreshApprovedAccessNotifications();
        const interval = setInterval(refreshApprovedAccessNotifications, 5000);

        return () => {
            clearInterval(interval);
        };
    }, [email, tenantName, refreshApprovedAccessNotifications]);

    useEffect(() => {
        if (Object.keys(pendingRequestDeviceIds).length === 0) {
            return;
        }

        const approvedDeviceIds = new Set(
            userApprovedNotifications
                .filter((item: any) => item.requestStatus === "APPROVED" && item.resourceName === "Whiteswan Access")
                .map((item: any) => String(item.computerId))
        );

        setPendingRequestDeviceIds((prev) => {
            const next = { ...prev };
            let changed = false;

            Object.keys(next).forEach((deviceId) => {
                if (approvedDeviceIds.has(deviceId)) {
                    delete next[deviceId];
                    changed = true;
                }
            });

            return changed ? next : prev;
        });
    }, [userApprovedNotifications, pendingRequestDeviceIds]);
    useEffect(() => {
        const timer = setInterval(() => {
            setRemainingSeconds((prev) => {
                if (prev <= 0) return 0;
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, []);

    const getOperatingModeColor = (mode: string, osMode: string) => {
        let status = labelStatus(mode, osMode);
        switch (status.toLowerCase()) {
            case "enforcing":
                return "destructive";
            case "auditing":
                return "default";
            case "disabled":
                return "secondary";
            default:
                return "secondary";
        }
    };

    const totalPages = Math.ceil(totalDevicesCount / DEVICES_PER_PAGE);

    const handlePageChange = (page: number) => {
        setPage(page);
        setCurrentPage(page);
    };

    const renderSkeletonCards = () => {
        return Array.from({ length: DEVICES_PER_PAGE }).map((_, index) => (
            <Card
                key={`skeleton-${index}`}
                className="border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900"
            >
                <CardContent className="p-6 space-y-4">
                    <div className="flex items-center gap-4">
                        <Skeleton className="w-12 h-12 rounded-xl" />
                        <div className="flex-1 space-y-2">
                            <Skeleton className="w-3/4 h-5" />
                            <Skeleton className="w-1/2 h-4" />
                        </div>
                    </div>
                    <div className="space-y-3">
                        <div className="flex items-center justify-between">
                            <Skeleton className="w-24 h-4" />
                            <Skeleton className="w-16 h-4" />
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <Skeleton className="w-16 h-5 rounded-full" />
                        <Skeleton className="w-20 h-5 rounded-full" />
                    </div>
                    <Skeleton className="w-full h-9 mt-4 rounded-md" />
                </CardContent>
            </Card>
        ));
    };


    return (
        <div className="space-y-4">
            {/* Header */}
            <div className="flex items-center justify-between p-4 bg-gradient-to-r from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 rounded-lg border">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center">
                        <Monitor className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                        <h3 className="font-semibold text-slate-900 dark:text-slate-100">
                            Available Devices
                        </h3>
                        <p className="text-sm text-slate-600 dark:text-slate-400">
                            {isLoading
                                ? "Loading devices..."
                                : `${totalDevicesCount} device${totalDevicesCount !== 1 ? "s" : ""} assigned to your roles`}
                        </p>
                    </div>
                </div>
                {isLoading && <Skeleton className="w-12 h-4 ml-auto" />}
            </div>

            {/* Device Cards Grid */}
            <div className="grid gap-4 grid-cols-2 lg:grid-cols-3">
                {isLoading ? (
                    renderSkeletonCards()
                ) : (
                    devices.map((device) => (
                        <Card
                            key={device.id}
                            className="hover:shadow-lg transition-all duration-200 cursor-pointer border-slate-200 dark:border-slate-700 hover:border-blue-300 dark:hover:border-blue-600 bg-white dark:bg-slate-900"
                            onClick={() => {
                                if (!device.isManuallyAddedMachine)
                                    handleDeviceClick(device.id)
                            }}
                        >
                            <CardContent className="p-6">
                                {/* Device Header */}
                                <div className="flex items-start justify-between mb-4">
                                    <div className="flex items-center space-x-4">
                                        <div className="w-12 h-12  flex items-center justify-center">
                                            {getOSIcon(device.os)}
                                        </div>
                                        <div className="flex-1">

                                            <h4
                                                className="font-semibold text-lg text-slate-900 dark:text-slate-100"
                                                data-testid={`text-device-name-${device.id}`}
                                            >
                                                {device.friendlyName ? device.friendlyName : device.name}
                                            </h4>
                                            {device.friendlyName && (
                                                <div className="text-xs text-muted-foreground">
                                                    Host Name: {device.name}
                                                </div>
                                            )}
                                            <div className="flex items-center gap-2 mt-1">
                                                <StatusIndicator lastSeen={device.lastSeen} showDot isManualDevice={device?.isManuallyAddedMachine || false} />
                                                <span className="text-sm text-slate-600 dark:text-slate-400">
                                                    {device.os}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Device Details */}
                                <div className="space-y-3 mb-4">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            <Cpu className="w-4 h-4 text-slate-500" />
                                            <span className="text-sm text-slate-600 dark:text-slate-400">
                                                Agent
                                            </span>
                                        </div>
                                        <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                                            v{device.agentVersion}
                                        </span>
                                    </div>
                                </div>

                                {/* Status Pills */}
                                <div className="flex flex-wrap gap-2 mb-4">
                                    <Badge variant="secondary" className="text-xs">
                                        {device.profile}
                                    </Badge>
                                    <Badge
                                        variant={getOperatingModeColor(device.mode || "", device.profileMode || "")}
                                        className="text-xs capitalize"
                                    >
                                        {labelStatus(device.mode || "", device.profileMode || "")}
                                    </Badge>
                                </div>

                                {/* Action Button */}
                                <div
                                    className="pt-3 border-t border-slate-100 dark:border-slate-700"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    {isOwnDevices(device) ?
                                        <div className="flex justify-end flex-wrap gap-2">
                                            <Badge
                                                variant="default"
                                                className="flex items-center gap-1 text-xs capitalize px-2 py-1"
                                            >
                                                <Check className="h-3 w-3" />
                                                ASSIGNED
                                            </Badge>
                                        </div> :
                                        shouldShowConnectButton(device) ?
                                            <div className="space-y-2 flex flex-col items-end">
                                                <Button
                                                    size="sm"
                                                    disabled={deviceLoadingState[device.id]?.loading}
                                                    onClick={() => handleConnect(device.id)}
                                                    data-testid={`button-connect-${device.id}`}
                                                    variant="default"
                                                    className="w-1/3 gap-2"
                                                >
                                                    <ShieldCheck className="w-4 h-4" />Connect
                                                    {deviceLoadingState[device.id]?.loading && <Loader2 className="w-4 h-4 text-white-600 dark:text-white-400 animate-spin" />}

                                                </Button>
                                                {remainingTimes[device.id] && (
                                                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                                                        <Clock className="w-3 h-3" />
                                                        <span>{remainingTimes[device.id]}</span>
                                                    </div>
                                                )}
                                            </div>
                                            : <Button
                                                size="sm"
                                                onClick={() => handleConnect(device.id)}
                                                data-testid={`button-connect-${device.id}`}
                                                variant="outline"
                                                className="w-full gap-2 border-blue-200 dark:border-blue-700 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 hover:border-blue-300 dark:hover:border-blue-600 bg-blue-50 dark:bg-blue-950/30 hover:bg-blue-100 dark:hover:bg-blue-950/50"
                                            >
                                                <ShieldCheck className="w-4 h-4" />
                                                Request Connect Access
                                            </Button>}

                                </div>
                            </CardContent>
                        </Card>
                    ))
                )}
            </div>

            {/* Empty State */}
            {!isLoading && devices.length === 0 && (
                <Card className="border-2 border-dashed border-slate-300 dark:border-slate-600">
                    <CardContent className="p-12 text-center">
                        <Monitor className="w-12 h-12 mx-auto mb-4 text-slate-400" />
                        <h3 className="text-lg font-medium text-slate-600 dark:text-slate-400 mb-2">
                            No devices available
                        </h3>
                        <p className="text-slate-500 dark:text-slate-500">
                            No devices are currently assigned to your roles.
                        </p>
                    </CardContent>
                </Card>
            )}

            {totalPages > 1 && (
                <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-900 rounded-lg border">
                    <div className="text-sm text-slate-600 dark:text-slate-400">
                    </div>
                    <div className="flex items-center gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage <= 1}
                            className="gap-1"
                            data-testid="button-prev-page"
                        >
                            <ChevronLeft className="w-4 h-4" />
                            Previous
                        </Button>
                        <div className="flex items-center gap-1">
                            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                const page = i + 1;
                                return (
                                    <Button
                                        key={page}
                                        variant={currentPage === page ? "default" : "outline"}
                                        size="sm"
                                        onClick={() => handlePageChange(page)}
                                        className="w-8 h-8 p-0"
                                        data-testid={`button-page-${page}`}
                                    >
                                        {page}
                                    </Button>
                                );
                            })}
                            {totalPages > 5 && (
                                <>
                                    <span className="text-slate-500 px-2">...</span>
                                    <Button
                                        variant={currentPage === totalPages ? "default" : "outline"}
                                        size="sm"
                                        onClick={() => handlePageChange(totalPages)}
                                        className="w-8 h-8 p-0"
                                        data-testid={`button-page-${totalPages}`}
                                    >
                                        {totalPages}
                                    </Button>
                                </>
                            )}
                        </div>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage >= totalPages}
                            className="gap-1"
                            data-testid="button-next-page"
                        >
                            Next
                            <ChevronRight className="w-4 h-4" />
                        </Button>
                    </div>
                </div>
            )}

            {/* Request Access Dialog */}
            <RequestConnectAccessDialog
                open={showAccessDialog}
                onOpenChange={setShowAccessDialog}
                onSubmitSuccess={handleRequestSubmitSuccess}
                deviceName={selectedDevice?.name || ""}
                applicationName="Device Access"
                osType={selectedDevice ? getOSType(selectedDevice) : "windows"}
                userList={usersList}
                sessionRequest={true}
                resourceType="GROUP"
                resourcseId={selectedDevice?.whiteswanAccessGroupId}
                isWhitewanAccess={"true"}
                isFromEndPoint={true}
                isFromEndPointApp={false}
                tenantName={tenantName}
                device={selectedDevice}
                publisherId={""}
                resourceName={"Whiteswan Access"}
                resourcePath={"Whiteswan Accesss"}
            />

        </div>
    );
}
